import {Modal, Button, Spinner, Form, Table, Pagination} from 'react-bootstrap'
import {FormattedMessage, defineMessages, useIntl} from 'react-intl'
import {formatPhoneNumber, formatUserOptionLabel, isAdministrator, parseId} from '../../../helpers'
import {useEffect, useState} from 'react'
import {toast} from 'react-toastify'
import {IResAssignLeads} from '../../../types/leads'
import {REASSIGN_LEADS_USER} from '../../../gql/mutations/leadMutations'
import {useLazyQuery, useMutation, useQuery} from '@apollo/client'
import {
  IAllUserNode,
  IPipelineGroupsPipeline,
  IResAllListUsers,
} from '../../../types/users'
import {GET_ALL_USERS_LIST} from '../../../gql/queries/usersQuery'
import {GET_ALL_LIST_LEADS, GET_TOTAL_LEADS_BY_USER} from '../../../gql/queries/leadsQuery'
import {IResAllListLead, ITotalLeads} from '../leadTypes'
import moment from 'moment'
import {IResPipelines, IStagePipelineSet} from '../../../types/pipelines'
import {useGetUser} from '../../../store/userStore'
import {URL} from '../../../gql/client'
import {GET_ALL_PIPELINES, GET_GROUPS_PIPELINE} from '../../../gql/queries/pipelines'

interface ModalReasingLeadsProps {
  isOpen: boolean
  onClose: () => void
  updateListLeads: () => void
  info: {
    fromUserId: string
    fromUserName: string
    pipelineId: string
    pipelineName: string
    statePipeline: string
    stateNamePipeline: string
    searchName: string
    searchPhone: string
    dateFrom: Date | null
    dateTo: Date | null
    assignType: string
    leadsSelected?: {id: string; name: string; stage: string; user: string}[]
  }
  iStagePipeline: IStagePipelineSet[] | null
  isExport: boolean
}

const space = ' '
const hash = '#'
const slash = '/'

const rM = defineMessages({
  byUser: {id: 'leads.reassign.byUser', defaultMessage: 'Reassign leads by user'},
  byFilters: {id: 'leads.reassign.byFilters', defaultMessage: 'Reassign leads by filters:'},
  bySelected: {id: 'leads.reassign.bySelected', defaultMessage: 'Reassign selected leads'},
  exportTitle: {id: 'leads.reassign.export', defaultMessage: 'Export leads'},
  leadsList: {id: 'leads.reassign.leadsList', defaultMessage: 'Leads lists'},
  leadsToAssign: {id: 'leads.reassign.leadsToAssign', defaultMessage: '{count, plural, one {# lead to assign} other {# leads to assign}}'},
  groupAssignment: {id: 'leads.reassign.groupAssignment', defaultMessage: 'Group Assignment (Round Robin)'},
  toStage: {id: 'leads.reassign.toStage', defaultMessage: 'To stage'},
  reassignAction: {id: 'leads.reassign.action', defaultMessage: 'Reassign leads'},
  exportError: {id: 'leads.reassign.exportError', defaultMessage: 'Error exporting'},
  filterPipeline: {id: 'leads.reassign.filter.pipeline', defaultMessage: 'Pipeline [{name}]'},
  filterDatesFromTo: {id: 'leads.reassign.filter.datesFromTo', defaultMessage: 'Dates from [{from}] to [{to}]'},
  filterDateFrom: {id: 'leads.reassign.filter.dateFrom', defaultMessage: 'Date from [{from}]'},
  filterDateTo: {id: 'leads.reassign.filter.dateTo', defaultMessage: 'Date to [{to}]'},
  filterFromStage: {id: 'leads.reassign.filter.fromStage', defaultMessage: 'from stage [{stage}]'},
  filterFromUser: {id: 'leads.reassign.filter.fromUser', defaultMessage: 'from user [{user}]'},
  filterNameContains: {id: 'leads.reassign.filter.nameContains', defaultMessage: 'name contains "{name}"'},
  filterPhoneContains: {id: 'leads.reassign.filter.phoneContains', defaultMessage: 'phone contains "{phone}"'},
})

export const ModalReasingLeads = ({
  isOpen,
  onClose,
  info,
  updateListLeads,
  iStagePipeline,
  isExport,
}: ModalReasingLeadsProps) => {
  const intl = useIntl()
  const errMsg = (message?: string) =>
    intl.formatMessage({id: 'common.error.withMessage', defaultMessage: 'Error: {message}'}, {message})
  const firstML = 20
  const [offsetML, setOffsetML] = useState(0)

  const [disabledAssign, setDisabledAssign] = useState(true)

  const [userFrom, setUserFrom] = useState('')
  const [userTo, setUserTo] = useState('')
  const [stageTo, setStageTo] = useState('')
  const [groupTo, setGroupTo] = useState('')
  const [byFiltersApplied, setFiltersApplied] = useState('')
  const [selectedPipelineTo, setSelectedPipelineTo] = useState('')

  const [filteredUsers, setFilteredUsers] = useState<IAllUserNode[] | undefined>(undefined)
  const user = useGetUser((s) => s.user)
  const isAdmin = isAdministrator(user?.permissions || [])
  const [loadingExportLeads, setLoadingExportLeads] = useState(false)

  const {
    data: dataMembersFrom,
    loading: loadingMember,
    error: errorMembers,
  } = useQuery<IResAllListUsers>(GET_ALL_USERS_LIST, {
    fetchPolicy: 'no-cache',
  })

  const {data: allPipelinesData} = useQuery<IResPipelines>(GET_ALL_PIPELINES, {
    skip: !isAdmin || !isOpen,
    fetchPolicy: 'cache-first',
  })

  const [
    gqlGetGroupsPipeline,
    {data: dataGroupPipeline, loading: loadingGroupPipelin, error: errorGroupPipeline},
  ] = useLazyQuery<IPipelineGroupsPipeline>(GET_GROUPS_PIPELINE, {fetchPolicy: 'no-cache'})

  const [getTotalLeads, {data: countTotalLeads, loading: loadingMemberLU, error: errorMembersLu}] =
    useLazyQuery<ITotalLeads>(GET_TOTAL_LEADS_BY_USER, {
      fetchPolicy: 'no-cache',
    })

  const [gqlGetLeads, {data: allDataLeads, loading: loadingAllLeads}] =
    useLazyQuery<IResAllListLead>(GET_ALL_LIST_LEADS, {fetchPolicy: 'no-cache'})

  const [gqlCall, {data, loading, error}] = useMutation<IResAssignLeads>(REASSIGN_LEADS_USER)

  const safeParseId = (value: any): number | null => {
    const id = parseId(value) as number
    return !id || isNaN(id) || id === 0 ? null : id
  }

  const handleDownload = async () => {
    if (!user) return
    const {token} = user
    if (!token) return
    const myHeaders = new Headers()
    myHeaders.append('Authorization', `Bearer ${token}`)

    const requestOptions = {
      method: 'GET',
      headers: myHeaders,
    }

    const stagePipelineId = safeParseId(info.statePipeline)
    const pipelineId = safeParseId(info.pipelineId)
    const startDate = info.dateFrom ? moment(info.dateFrom).format('YYYY-MM-DD') : null
    const endDate = info.dateTo ? moment(info.dateTo).format('YYYY-MM-DD') : null
    const userId = safeParseId(info.fromUserId)
    const name = info.searchName
    const phone = info.searchPhone

    const rawParams: Record<string, string | null | undefined> = {
      stage_pipeline_id: stagePipelineId ? '' + stagePipelineId : null,
      stage_pipeline__pipeline_id: pipelineId ? '' + pipelineId : null,
      start_date: startDate,
      end_date: endDate,
      user_id: userId ? '' + userId : null,
      name: name,
      phone: phone,
    }

    const params = new URLSearchParams()

    Object.entries(rawParams).forEach(([key, value]) => {
      if (value != null && value !== '') {
        params.append(key, value)
      }
    })

    setLoadingExportLeads(true)
    try {
      const url = `${URL}/export-filtered-leads-excel?${params.toString()}`
      const res = await fetch(url, requestOptions)
      if (!res.ok) throw new Error(intl.formatMessage(rM.exportError))
      const blob = await res.blob()
      const downloadUrl = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = downloadUrl
      link.download = `leads-${moment().format('DD-MM-YY')}.xlsx`
      document.body.appendChild(link)
      setLoadingExportLeads(false)
      link.click()
    } catch (err) {
      setLoadingExportLeads(false)
      toast.error(errMsg((err as {message: string}).message))
    }
  }
  useEffect(() => {
    if (!info.pipelineId) return
    gqlGetGroupsPipeline({
      variables: {
        id: parseId(info.pipelineId),
      },
    })
  }, [gqlGetGroupsPipeline, info.pipelineId])

  useEffect(() => {
    const filteredUsersA = dataMembersFrom?.allUsersList.filter((user) => user.id !== userFrom)
    setFilteredUsers(filteredUsersA)

    if (userFrom)
      getTotalLeads({variables: {pipelineId: parseId(info.pipelineId), userId: parseId(userFrom)}})
  }, [dataMembersFrom, getTotalLeads, info.pipelineId, userFrom])

  useEffect(() => {
    if (!info.pipelineId) return

    if (isExport) {
      handleDownload()
      onClose()
    }

    if (info.fromUserId) {
      const filteredUsersA = dataMembersFrom?.allUsersList.filter(
        (user) => user.id !== info.fromUserId
      )
      setFilteredUsers(filteredUsersA)
    } else {
      const filteredUsersA = dataMembersFrom?.allUsersList
      setFilteredUsers(filteredUsersA)
    }

    if (info.assignType === 'by-filters' || isExport) {
      let filtersApplied = ''
      if (isExport) {
        filtersApplied += intl.formatMessage(rM.filterPipeline, {name: info.pipelineName})
      }

      if (info.dateFrom && info.dateTo)
        filtersApplied += intl.formatMessage(rM.filterDatesFromTo, {
          from: moment(info.dateFrom).format('DD-MMM-YY'),
          to: moment(info.dateTo).format('DD-MMM-YY'),
        })
      else if (info.dateFrom)
        filtersApplied +=
          (filtersApplied.length > 0 ? ', ' : '') +
          intl.formatMessage(rM.filterDateFrom, {from: moment(info.dateFrom).format('DD/MMM/YY')})
      else if (info.dateTo)
        filtersApplied +=
          (filtersApplied.length > 0 ? ', ' : '') +
          intl.formatMessage(rM.filterDateTo, {to: moment(info.dateTo).format('DD/MMM/YY')})

      if (info.statePipeline)
        filtersApplied +=
          (filtersApplied.length > 0 ? ', ' : '') +
          intl.formatMessage(rM.filterFromStage, {stage: info.stateNamePipeline})

      if (info.fromUserId && info.fromUserName)
        filtersApplied +=
          (filtersApplied.length > 0 ? ', ' : '') +
          intl.formatMessage(rM.filterFromUser, {user: info.fromUserName})

      if (info.searchName)
        filtersApplied +=
          (filtersApplied.length > 0 ? ', ' : '') +
          intl.formatMessage(rM.filterNameContains, {name: info.searchName})

      if (info.searchPhone)
        filtersApplied +=
          (filtersApplied.length > 0 ? ', ' : '') +
          intl.formatMessage(rM.filterPhoneContains, {phone: info.searchPhone})

      setFiltersApplied(filtersApplied)
      console.log('gqlGetLeads By Filters', filtersApplied)
      gqlGetLeads({
        variables: {
          pipelineId: parseId(info.pipelineId),
          userId: parseId(info.fromUserId ?? ''),
          stagePipelineId:
            (parseId(info.statePipeline) as number) > 0
              ? (parseId(info.statePipeline) as number)
              : null,
          name_Icontains: info.searchName,
          phone_Icontains: info.searchPhone,
          fromDate: info.dateFrom != null ? moment(info.dateFrom).format('YYYY-MM-DD') : null,
          toDate: info.dateTo != null ? moment(info.dateTo).format('YYYY-MM-DD') : null,
          first: firstML,
          offset: offsetML,
        },
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    gqlGetLeads,
    info.assignType,
    info.dateFrom,
    info.dateTo,
    info.fromUserId,
    info.pipelineId,
    info.searchName,
    info.searchPhone,
    info.statePipeline,
    offsetML,
    firstML,
    info.stateNamePipeline,
    info.fromUserName,
    isExport,
    info.pipelineName,
  ])

  useEffect(() => {
    if (error) toast.error(errMsg(error.message))
    if (errorMembers) toast.error(errMsg(errorMembers.message))
    if (errorGroupPipeline) toast.error(errMsg(errorGroupPipeline.message))
    if (errorMembersLu) toast.error(errMsg(errorMembersLu.message))
  }, [error, errorMembers, errorMembersLu, errorGroupPipeline])

  useEffect(() => {
    setUserFrom(info.fromUserId)
  }, [info.fromUserId])

  useEffect(() => {
    setDisabledAssign(true)
    if ((userTo && !groupTo) || (!userTo && groupTo) || stageTo) {
      if (byFiltersApplied || userFrom || (info.leadsSelected && info.leadsSelected.length > 0)) {
        setDisabledAssign(false)
      }
    }
  }, [userTo, stageTo, byFiltersApplied, info.leadsSelected, userFrom, groupTo])

  useEffect(() => {
    if (!data) return
    if (data.assignLeadsToUser?.success) {
      toast.success(data.assignLeadsToUser?.message)
      updateListLeads()
      modalClose()
    } else if (data.assignLeadsToUser?.errors && data.assignLeadsToUser?.errors.length > 0) {
      toast.error(errMsg(data.assignLeadsToUser?.errors[0]?.message))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  const handleReAssignLeads = () => {
    if (isExport) return
    if (!info.pipelineId) return

    const leadIds: (number | null)[] = (info.leadsSelected ?? [])
      .map((lead) => safeParseId(lead.id))
      .filter((id): id is number => id !== null) // eliminamos los nulls si no querés enviarlos

    gqlCall({
      variables: {
        pipelineId: safeParseId(info.pipelineId),
        fromUserId: safeParseId(userFrom),
        fromStagePipelineId: safeParseId(info.statePipeline),
        fromDate: info.dateFrom ? moment(info.dateFrom).format('YYYY-MM-DD') : null,
        toDate: info.dateTo ? moment(info.dateTo).format('YYYY-MM-DD') : null,
        name_Icontains: info.searchName,
        phone_Icontains: info.searchPhone,
        toUserId: safeParseId(userTo),
        toStagePipelineId: safeParseId(stageTo),
        toGroupId: safeParseId(groupTo),
        leadIds,
      },
    })
  }

  const modalClose = (): void => {
    info = {
      fromUserId: '',
      fromUserName: '',
      pipelineId: '',
      pipelineName: '',
      statePipeline: '',
      stateNamePipeline: '',
      searchName: '',
      searchPhone: '',
      dateFrom: null,
      dateTo: null,
      assignType: '',
      leadsSelected: [],
    }
    isExport = false
    setSelectedPipelineTo('')
    setStageTo('')
    onClose()
  }

  return (
    <Modal show={isOpen} onHide={modalClose} size='xl' className='modal-reassing-leads'>
      <Modal.Header closeButton>
        <Modal.Title>
          <div className='d-flex flex-column gap-0'>
            <span className='fw-bold '>
              {info.assignType === 'by-user' ? intl.formatMessage(rM.byUser) : ''}
              {info.assignType === 'by-filters' ? intl.formatMessage(rM.byFilters) : ''}
              {info.assignType === 'by-leads-selected' ? intl.formatMessage(rM.bySelected) : ''}
            </span>
            {info.assignType === 'by-filters' && (
              <span className='filters-applied'>{byFiltersApplied}</span>
            )}
            {isExport && (
              <>
                <span className='fw-bold '>{intl.formatMessage(rM.exportTitle)}</span>
                <span className='filters-applied'>{byFiltersApplied}</span>
              </>
            )}
          </div>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className='d-flex flex-column gap-4'>
          <div className='reassign-leads reassign-from'>
            {info.assignType === 'by-user' && (
              <h2 className='fs-1'>
                <FormattedMessage id='common.field.from' defaultMessage='From' />{space}
                {loadingMember && (
                  <span>
                    <Spinner animation='border' size='sm' />
                  </span>
                )}
              </h2>
            )}

            {/* Solo mientras carga: al llegar la lista, el título lo pone la fila
                que lo comparte con la paginación. */}
            {(info.assignType === 'by-filters' || isExport) && loadingAllLeads && (
              <h2 className='fs-1'>
                {intl.formatMessage(rM.leadsList)}{space}
                <span>
                  <Spinner animation='border' size='sm' />
                </span>
              </h2>
            )}
            {!loadingMember &&
              !allDataLeads &&
              dataMembersFrom &&
              info.assignType !== 'by-leads-selected' &&
              info.fromUserId && (
                <>
                  <div>
                    <Form.Select aria-label={intl.formatMessage({id: 'leads.form.leadSource', defaultMessage: 'Lead source'})} name='leadSource'>
                      <option value={info.fromUserId}>{info.fromUserName}</option>
                    </Form.Select>
                  </div>
                </>
              )}
            {!loadingMember &&
              !loadingAllLeads &&
              !allDataLeads &&
              dataMembersFrom &&
              !(info.leadsSelected && info.leadsSelected?.length > 0) &&
              !info.fromUserId && (
                <div>
                  <Form.Select
                    aria-label={intl.formatMessage({id: 'leads.form.leadSource', defaultMessage: 'Lead source'})}
                    name='leadSource'
                    value={userFrom}
                    onChange={(e) => {
                      const {value} = e.target
                      if (value) {
                        setUserFrom(value)
                      }
                      if (e.target.value === '') {
                        setUserFrom('')
                      }
                    }}
                  >
                    <option value=''>{intl.formatMessage({id: 'common.placeholder.selectOption', defaultMessage: 'Select an option'})}</option>
                    {dataMembersFrom?.allUsersList
                      .filter((node) => node.id !== info.fromUserId)
                      .map((node) => (
                        <option key={node.id} value={node.id}>
                          {formatUserOptionLabel(node)}
                        </option>
                      ))}
                  </Form.Select>
                </div>
              )}
            {info.assignType === 'by-user' && loadingMemberLU && (
              <span>
                <Spinner animation='border' size='sm' />
              </span>
            )}
            {countTotalLeads && userFrom && userFrom !== '' && info.assignType === 'by-user' && (
              <div className='total-leads-assign-to'>
                {intl.formatMessage(rM.leadsToAssign, {count: Number(countTotalLeads.totalLeads ?? 0)})}
              </div>
            )}
            {!loadingAllLeads &&
              (allDataLeads || (info.leadsSelected && info.leadsSelected?.length > 0)) && (
                <div>
                  <div className='d-flex justify-content-between mb-2'>
                    <h2 className='fs-1'>{intl.formatMessage(rM.leadsList)}</h2>
                    {(info.assignType === 'by-filters' || isExport) && (
                      <div className='d-flex align-items-center justify-content-end'>
                        <Pagination size='sm'>
                          <Pagination.Prev
                            disabled={offsetML === 0}
                            onClick={() => {
                              setOffsetML((p) => p - firstML)
                            }}
                          />
                          {
                            <span className='d-flex align-items-center text-secondary'>
                              {offsetML / firstML + 1}{slash}
                              {allDataLeads?.totalLeads ? (
                                Math.ceil(allDataLeads?.totalLeads / firstML)
                              ) : allDataLeads?.totalLeads == null ? (
                                <Spinner animation='grow' size='sm' />
                              ) : (
                                allDataLeads?.totalLeads
                              )}
                            </span>
                          }
                          <Pagination.Next
                            disabled={!allDataLeads?.allLeads.pageInfo.hasNextPage}
                            onClick={() => {
                              setOffsetML((p) => p + firstML)
                            }}
                          />
                        </Pagination>
                      </div>
                    )}
                    <span
                      className='d-flex align-items-center justify-content-center px-3 py-1 text-primary'
                      style={{
                        borderRadius: '20px',
                        backgroundColor: '#ddf1ff',
                        fontWeight: 'bold',
                      }}
                    >
                      {(info.assignType === 'by-filters' || isExport) && allDataLeads?.totalLeads}
                      {info.assignType === 'by-leads-selected' &&
                      info.leadsSelected &&
                      info.leadsSelected?.length > 0
                        ? info.leadsSelected?.length
                        : ''}
                    </span>
                  </div>
                  <div className='table-scrollable table-container'>
                    <Table
                      hover
                      size='sm'
                      className='table-scrollable table-scroll table-light table-striped table-bordered table-sm'
                    >
                      <thead>
                        <tr role='row'>
                          <th className='fw-bold text-center'>{hash}</th>
                          <th className='fw-bold'><FormattedMessage id='common.field.name' defaultMessage='Name' /></th>
                          <th className='fw-bold'><FormattedMessage id='leads.list.summary.stage' defaultMessage='Stage' /></th>
                          <th className='fw-bold'><FormattedMessage id='common.field.user' defaultMessage='User' /></th>
                        </tr>
                      </thead>
                      <tbody style={{maxHeight: '300px', overflowY: 'auto'}}>
                        {allDataLeads?.allLeads?.edges
                          .slice() // Create a copy before sorting (Apollo cache arrays are immutable)
                          .sort((a, b) => a.node.stagePipeline.order - b.node.stagePipeline.order)
                          .map((lead, index) => (
                            <tr className='cursor-pointer' key={lead.node.id}>
                              <td className='text-center'>{index + offsetML + 1}</td>
                              <td>
                                {lead.node.name}
                                {info.searchPhone ? (
                                  <span className='fst-italic fs-7 p-2'>
                                    <i className='bi bi-phone fs-8'></i>
                                    {formatPhoneNumber(lead.node.phone)}
                                  </span>
                                ) : (
                                  ''
                                )}
                              </td>
                              <td>{lead.node.stagePipeline.name}</td>
                              <td>
                                {lead.node.user?.edges[0]?.node.firstName}{space}
                                {lead.node.user?.edges[0]?.node.lastName}
                              </td>
                            </tr>
                          ))}
                        {info.leadsSelected?.map((lead, index) => (
                          <tr className='cursor-pointer' key={lead.id}>
                            <td className='text-center'>{index + 1}</td>
                            <td>{lead.name}</td>
                            <td>{lead.stage}</td>
                            <td>{lead.user ?? ''}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                </div>
              )}
          </div>
          {!isExport && (
            <div className='reassign-leads reassign-to'>
              <h2 className='fs-1'><FormattedMessage id='common.field.to' defaultMessage='To' /></h2>

              <div className='row'>
                <div className='col reassign-to-group'>
                  <h4 className='fs-4'>
                    {intl.formatMessage(rM.groupAssignment)}{space}
                    {loadingGroupPipelin && (
                      <span>
                        <Spinner animation='border' size='sm' />
                      </span>
                    )}{space}
                  </h4>

                  {!loadingMember &&
                    ((userFrom && userFrom !== '') || allDataLeads || info.leadsSelected) && (
                      <div>
                        <Form.Select
                          aria-label={intl.formatMessage({id: 'leads.form.leadSource', defaultMessage: 'Lead source'})}
                          disabled={userTo !== ''}
                          name='leadSource'
                          onChange={(e) => {
                            const {value} = e.target
                            if (value) {
                              setGroupTo(value)
                            }
                            if (e.target.value === '') {
                              setGroupTo('')
                            }
                          }}
                        >
                          <option value=''>{intl.formatMessage({id: 'common.placeholder.selectOption', defaultMessage: 'Select an option'})}</option>
                          {dataGroupPipeline?.pipelineById?.groups.edges?.map(({node}) => (
                            <option key={node.id} value={node.id}>
                              {node.name}
                            </option>
                          ))}
                        </Form.Select>
                      </div>
                    )}
                </div>
                <div className='col reassign-to-user'>
                  <h4 className='fs-4 text-primary'>
                    <FormattedMessage id='common.field.user' defaultMessage='User' />{space}
                    {loadingMember && (
                      <span>
                        <Spinner animation='border' size='sm' />
                      </span>
                    )}{space}
                  </h4>
                  {!loadingMember &&
                    ((userFrom && userFrom !== '') || allDataLeads || info.leadsSelected) && (
                      <div>
                        <Form.Select
                          aria-label={intl.formatMessage({id: 'leads.form.leadSource', defaultMessage: 'Lead source'})}
                          name='leadSource'
                          disabled={groupTo !== ''}
                          onChange={(e) => {
                            const {value} = e.target
                            if (value) {
                              setUserTo(value)
                            }
                            if (e.target.value === '') {
                              setUserTo('')
                            }
                          }}
                        >
                          <option value=''>{intl.formatMessage({id: 'common.placeholder.selectOption', defaultMessage: 'Select an option'})}</option>
                          {filteredUsers
                            ?.filter((node) => node.id !== info.fromUserId)
                            .map((node) => (
                              <option key={node.id} value={node.id}>
                                {formatUserOptionLabel(node)}
                              </option>
                            ))}
                        </Form.Select>
                      </div>
                    )}
                </div>
                <div className='col reassign-to-stage'>
                  <h4 className='fs-4'>
                    {intl.formatMessage(rM.toStage)}{space}
                    {loadingMember && (
                      <span>
                        <Spinner animation='border' size='sm' />
                      </span>
                    )}
                  </h4>
                  {!loadingMember &&
                    ((userFrom && userFrom !== '') || allDataLeads || info.leadsSelected) && (
                      <div className='d-flex flex-column gap-2'>
                        {isAdmin && (
                          <Form.Select
                            aria-label={intl.formatMessage({id: 'leads.list.pipeline', defaultMessage: 'Pipeline'})}
                            name='pipelineTo'
                            value={selectedPipelineTo}
                            onChange={(e) => {
                              setSelectedPipelineTo(e.target.value)
                              setStageTo('')
                            }}
                          >
                            <option value={info.pipelineId}>{info.pipelineName}</option>
                            {allPipelinesData?.allPipelines.edges
                              .filter((e) => e.node.id !== info.pipelineId)
                              .map(({node}) => (
                                <option key={node.id} value={node.id}>
                                  {node.name}
                                </option>
                              ))}
                          </Form.Select>
                        )}
                        <Form.Select
                          aria-label={intl.formatMessage({id: 'leads.list.summary.stage', defaultMessage: 'Stage'})}
                          name='stageTo'
                          value={stageTo}
                          onChange={(e) => {
                            const {value} = e.target
                            setStageTo(value)
                          }}
                        >
                          <option value=''>{intl.formatMessage({id: 'common.placeholder.selectOption', defaultMessage: 'Select an option'})}</option>
                          {(selectedPipelineTo && selectedPipelineTo !== info.pipelineId
                            ? allPipelinesData?.allPipelines.edges
                                .find((e) => e.node.id === selectedPipelineTo)
                                ?.node.stagePipelineSet.edges ?? null
                            : iStagePipeline
                          )
                            ?.slice()
                            .sort((a, b) => a.node.order - b.node.order)
                            .map(({node}) => (
                              <option key={node.id} value={node.id}>
                                {node.name}
                              </option>
                            ))}
                        </Form.Select>
                      </div>
                    )}
                </div>
              </div>
            </div>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button
          onClick={(e) => {
            modalClose()
          }}
          variant='light'
        >
          <FormattedMessage id='common.action.cancel' defaultMessage='Cancel' />
        </Button>
        {isExport && (
          <Button
            onClick={handleDownload}
            variant='primary'
            disabled={(loadingExportLeads || loadingAllLeads) && !allDataLeads}
          >
            {loading && <Spinner animation='border' className='me-2' size='sm' />}
            <i className='bi bi-download'></i>
            {intl.formatMessage(rM.exportTitle)}
          </Button>
        )}
        {!isExport && (
          <Button onClick={handleReAssignLeads} variant='primary' disabled={disabledAssign}>
            {loading && <Spinner animation='border' className='me-2' size='sm' />}
            <i className='bi bi-person-up' />
            {intl.formatMessage(rM.reassignAction)}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  )
}
